import { createContext, useContext, type ReactNode } from "react";

/** Shared nullable context bridge for feature-owned Workspace Surface renderers. */
export function createWorkspaceSurfaceContext<Value>() {
  const Context = createContext<Value | null>(null);

  function Provider(props: { readonly children: ReactNode; readonly value: Value | null }) {
    return <Context.Provider value={props.value}>{props.children}</Context.Provider>;
  }

  return {
    Provider,
    useValue: () => useContext(Context),
  } as const;
}

export interface DelegatedWorkspaceSurfaceContextValue<Descriptor> {
  readonly render: (descriptor: Descriptor) => ReactNode;
}

/**
 * Build the repeated provider/adapter seam for a feature rendered by the
 * Workspace Surface Host while keeping descriptor validation feature-owned.
 */
export function createDelegatedWorkspaceSurface<Descriptor, Selected extends Descriptor>(
  isSelected: (descriptor: Descriptor) => descriptor is Selected,
) {
  const context = createWorkspaceSurfaceContext<DelegatedWorkspaceSurfaceContextValue<Selected>>();

  function Surface({ descriptor }: { readonly descriptor: Descriptor }) {
    const value = context.useValue();
    if (value === null || !isSelected(descriptor)) return null;
    return value.render(descriptor);
  }

  return { Provider: context.Provider, Surface } as const;
}

export type WorkspaceSurfaceContentMap<Descriptor extends { readonly kind: string }> = Partial<
  Readonly<Record<Descriptor["kind"], ReactNode>>
>;

/** Build a provider/adapter seam for descriptor-kind keyed feature content. */
export function createMappedWorkspaceSurface<Descriptor extends { readonly kind: string }>() {
  const context = createWorkspaceSurfaceContext<WorkspaceSurfaceContentMap<Descriptor>>();

  function Surface({
    descriptor,
    fallback = null,
  }: {
    readonly descriptor: Descriptor;
    /** Rendered when nothing is registered for this kind, so the Region is never left blank. */
    readonly fallback?: ReactNode;
  }) {
    const content = context.useValue();
    return content?.[descriptor.kind as Descriptor["kind"]] ?? fallback;
  }

  return { Provider: context.Provider, Surface } as const;
}
